import React from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import type { CardDTO, CardQR } from "../../services/api/cardService";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { CardChip, CardGuilloche, CardMark } from "./CardChrome";
import { CARD_FRAME, CARD_SHADOW, cardMaterial } from "./cardStyle";

interface Props {
  cardData: CardDTO | null;
  qrData: CardQR | null;
  isQrLoading?: boolean;
  /**
   * First run (UX_CONCEPT.md §5.5): with no QR yet, this text sits in a dashed
   * frame instead of a loading shimmer, and the QR lands when it arrives.
   */
  qrPlaceholder?: string;
  /** First run: chips under the details, such as the blood type. */
  extras?: React.ReactNode;
}

/**
 * "MM/YYYY" like the expiry on a payment card: a card convention that reads
 * the same in every language, so it is not localised.
 */
function formatMonthYear(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function genderSymbol(
  gender: string | null,
  t: Translation,
): {
  symbol: string;
  label: string;
} {
  if (gender === "male") return { symbol: "♂", label: t.male };
  if (gender === "female") return { symbol: "♀", label: t.female };
  return { symbol: "⚥", label: t.cardxGenderNotStated };
}

function capitalise(value: string | null): string {
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const CardSkeleton: React.FC = () => (
  <div className="w-full max-w-[342px] aspect-[85.6/54] rounded-[18px] bg-primary-100 animate-pulse" />
);

export const HealthPassCardFront: React.FC<Props> = ({
  cardData,
  qrData,
  isQrLoading,
  qrPlaceholder,
  extras,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  if (!cardData) return <CardSkeleton />;

  const holder = cardData.holder ?? {
    firstName: "",
    lastName: "",
    avatarUrl: undefined,
    gender: null,
    dateOfBirth: null,
    nationality: null,
  };
  const material = cardMaterial(cardData.subscriptionTier);
  const initials =
    `${holder.firstName?.[0] ?? ""}${holder.lastName?.[0] ?? ""}`.toUpperCase();
  const gender = genderSymbol(holder.gender, t);
  const fullName = `${holder.firstName || "—"} ${holder.lastName || ""}`.trim();

  return (
    <div
      className={CARD_FRAME}
      style={{ background: material.background, boxShadow: CARD_SHADOW }}
      aria-label={t.cardxFrontAria.replace("{name}", fullName)}
      role="group"
    >
      <CardGuilloche color={material.guilloche} />

      {/* Top row: mark, wordmark, tier — chip */}
      <div className="absolute left-[18px] right-[18px] top-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CardMark className="h-5 w-5" />
          <span className="font-display text-sm font-bold tracking-[0.04em]">
            HEALTH PASS
          </span>
          <span
            className="rounded-full border px-1.5 py-px text-[9px] font-bold tracking-[0.14em]"
            style={{ borderColor: material.accent, color: material.accent }}
          >
            {material.tierLabel}
          </span>
        </div>
        <CardChip background={material.chip} />
      </div>

      {/* Middle: QR, then photo, name and details */}
      <div className="absolute left-[18px] right-[18px] top-[26%] flex items-center gap-3.5">
        <div
          className={`flex h-[84px] w-[84px] flex-shrink-0 items-center justify-center rounded-xl p-1.5 ${
            qrPlaceholder && !qrData?.qrDataUrl
              ? "border-2 border-dashed border-white/55"
              : "keep-light bg-white"
          }`}
          title={t.cardxQrAlt}
        >
          {qrData?.qrDataUrl && !isQrLoading ? (
            <img
              src={qrData.qrDataUrl}
              alt={t.cardxCardQrAlt}
              className={`h-full w-full object-contain ${qrPlaceholder ? "motion-safe:animate-stamp-in" : ""}`}
            />
          ) : qrPlaceholder ? (
            <span className="text-center text-[10px] font-semibold leading-tight text-white/85">
              {qrPlaceholder}
            </span>
          ) : (
            <div
              className="h-full w-full rounded-md bg-primary-50 animate-pulse"
              aria-label={t.cardxQrLoading}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#ffd8c4] text-sm font-bold text-[#7a2e0e] shadow-[0_0_0_2px_rgba(255,255,255,0.85)]">
              {holder.avatarUrl ? (
                <img
                  src={holder.avatarUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                initials
              )}
            </div>
            <div className="truncate font-display text-[19px] font-semibold leading-tight">
              {fullName}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-white/90">
            <span aria-label={gender.label}>{gender.symbol}</span>
            {/* Month in letters, so the date cannot be misread across
                countries (03/04 is March to some, April to others). */}
            <span>
              {holder.dateOfBirth
                ? fmt.date(holder.dateOfBirth, "medium") || "—"
                : "—"}
            </span>
          </div>
          <div className="mt-0.5 truncate text-xs text-white/90">
            {capitalise(holder.nationality)}
          </div>
          {extras && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">{extras}</div>
          )}
        </div>
      </div>

      {/* Bottom: card number, validity */}
      <div className="absolute bottom-3.5 left-[18px] right-[18px] flex items-end justify-between">
        <div>
          <div className="text-[8.5px] tracking-[0.16em] text-white/70">
            {t.cardxCardNumber}
          </div>
          <div className="mt-0.5 font-mono text-sm font-medium tracking-[0.08em] [text-shadow:0_1px_0_rgba(0,0,0,0.25)]">
            {cardData.cardNumber}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[8.5px] tracking-[0.16em] text-white/70">
            {t.cardxValidUntil}
          </div>
          <div className="mt-0.5 font-mono text-sm font-medium tracking-[0.08em]">
            {formatMonthYear(cardData.validUntil)}
          </div>
        </div>
      </div>
    </div>
  );
};
